import { type QueryStatus } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useMy } from '@/hooks';
import { useGetCollaboratorsListFromAccounts } from '@/hooks/useAccounts';
import { useCustomerType } from '@/hooks/useCustomerType';
import { defaultFilters } from '@/sections/customerManagement/config/columnDefinitions';
import {
  useCustomerIntegrationHealth,
  useCustomerRiskCounts,
  useLastCompromiseDate,
} from '@/sections/customerManagement/hooks/data';
import { useBulkCustomerStatus } from '@/sections/customerManagement/hooks/status';
import { useCustomerDisplayName } from '@/sections/customerManagement/hooks/useCustomerDisplayName';
import {
  CustomerDataWithMetrics,
  Filters,
  Status,
} from '@/sections/customerManagement/types';
import { useAuth } from '@/state/auth';
import { CustomerTypeValue, RiskSeverity } from '@/types';
import { mergeStatus } from '@/utils/api';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

const isValidStatus = (status: unknown): status is Status => {
  return (
    typeof status === 'string' &&
    ['Active', 'Paused', 'Setup', 'Upcoming', 'Completed'].includes(status)
  );
};

export const useCustomerManagement = () => {
  const { me } = useAuth();
  const [filters, setFilters] = useStorage<Filters>(
    {
      queryKey: 'customerManagementFilters.v3',
      key: StorageKey.FILTERS_CUSTOMER_MANAGEMENT,
    },
    defaultFilters
  );

  const {
    data: accounts,
    status: accountsStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMy(
    {
      resource: 'account',
    },
    {
      doNotImpersonate: true, // Customer management should always run as the actual user
    }
  );

  // Auto-fetch all pages of account data to ensure all customers are loaded.
  // Without this, only the first page from the API is displayed, causing
  // customers on subsequent pages to be missing from the dashboard.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allCollaborators = useGetCollaboratorsListFromAccounts(accounts);
  // Exclude the user's own personal tenant from the customer list
  const collaborators = allCollaborators.filter(c => c.name !== me);
  const typeQuery = useCustomerType(true); // doNotImpersonate for customer management

  // Only show accounts that have a CustomerType — real customers added via the
  // "Add Customer" flow always have a type. Individual user collaborators do not.
  // While types are loading, show all collaborators to avoid flashing empty state.
  const customerCollaborators = useMemo(
    () =>
      typeQuery.status !== 'success'
        ? collaborators
        : collaborators.filter(c => typeQuery.dataMap.has(c.name)),
    [collaborators, typeQuery.status, typeQuery.dataMap]
  );

  const customerStatusQuery = useBulkCustomerStatus(
    customerCollaborators.map(c => c.name),
    typeQuery.dataMap as Map<string, CustomerTypeValue>
  );
  const riskCountsQuery = useCustomerRiskCounts();
  const displayNameQuery = useCustomerDisplayName();
  const integrationHealthQuery = useCustomerIntegrationHealth();
  const lastCompromiseQuery = useLastCompromiseDate();

  const customers = useMemo<CustomerDataWithMetrics[]>(() => {
    return customerCollaborators.map(collaborator => {
      const email = collaborator.name;
      const healthData = integrationHealthQuery.dataMap.get(email);
      const rawStatus = customerStatusQuery.data.get(email) || 'Setup';
      const resolvedDisplayName = displayNameQuery.dataMap.get(email) || email;

      return {
        email,
        displayName: resolvedDisplayName,
        status: isValidStatus(rawStatus) ? rawStatus : 'Setup',
        type: typeQuery.dataMap.get(email) || undefined,
        critical:
          riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Critical}`) || 0,
        high: riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.High}`) || 0,
        medium:
          riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Medium}`) || 0,
        low: riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Low}`) || 0,
        info: riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Info}`) || 0,
        exposure: 0,
        healthyIntegrations: healthData?.healthyIntegrations || 0,
        totalIntegrations: healthData?.totalIntegrations || 0,
        health: healthData
          ? `${healthData.healthyIntegrations}/${healthData.totalIntegrations}`
          : '',
        riskCountsStatus: riskCountsQuery.status || 'pending',
        typeStatus: typeQuery.status || 'pending',
        integrationHealthStatus: integrationHealthQuery?.status || 'pending',
        statusStatus: customerStatusQuery.status || 'pending',
        lastCompromiseDate: lastCompromiseQuery.dataMap.get(email) || undefined,
        lastCompromiseStatus: lastCompromiseQuery.status || 'pending',
        subscriptionEndDate:
          customerStatusQuery.endDateMap?.get(email) || undefined,
        queryStatus: {
          riskCounts: riskCountsQuery.status || 'pending',
          integrationHealth: integrationHealthQuery?.status || 'pending',
          type: typeQuery.status || 'pending',
          status: customerStatusQuery.status || 'pending',
          lastCompromise: (lastCompromiseQuery.status ||
            'pending') as QueryStatus,
        },
      };
    });
  }, [
    customerCollaborators,
    customerStatusQuery,
    riskCountsQuery,
    integrationHealthQuery,
    typeQuery,
    displayNameQuery,
    lastCompromiseQuery,
  ]);

  const filteredCustomers = useMemo(() => {
    let filteredCollaborators = customers;
    if (filters.search) {
      filteredCollaborators = customers.filter(customer => {
        return (
          customer.displayName
            .toLowerCase()
            .includes(filters.search!.toLowerCase()) ||
          customer.email.toLowerCase().includes(filters.search!.toLowerCase())
        );
      });
    }
    if (filters.advancedFilters && filters.advancedFilters.status) {
      filteredCollaborators = filteredCollaborators.filter(customer => {
        return filters.advancedFilters!.status!.includes(customer.status);
      });
    }
    if (filters.advancedFilters && filters.advancedFilters.type) {
      filteredCollaborators = filteredCollaborators.filter(customer => {
        return customer.type && typeof customer.type === 'string'
          ? filters.advancedFilters!.type!.includes(customer.type)
          : false;
      });
    }
    return filteredCollaborators;
  }, [customers, filters]);

  return {
    filters,
    setFilters,
    customers: filteredCustomers,
    allCustomers: customers, // Raw unfiltered customer list
    customersStatus: mergeStatus(
      accountsStatus,
      // customerStatusQuery.status,
      displayNameQuery.status
    ),
    totalCustomers: customerCollaborators.length,
  };
};
